import { clsx } from "@/src/utils/clsx"

/**
 * Keyboard focus ring. Stroke is 2px `col-border-strong` (#000) on light
 * surfaces — 21:1 against white, so it meets WCAG 2.4.7, 1.4.11, and
 * 2.4.13 AAA size. A matching global `:focus-visible` rule in
 * `app/globals.css` covers controls that do not import this helper.
 */
export const FOCUS_VISIBLE_CLASS = clsx(
    "outline-none",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-col-border-strong"
)

/** White 2px ring on dark surfaces. Black would fail 1.4.11 on black. */
export const FOCUS_VISIBLE_INVERTED_CLASS = clsx(
    "outline-none",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-col-border-inverted"
)

/**
 * Opt out of the global outline. Use when the control already has a
 * rounded box border — the idle border turns `col-border-strong` on
 * `:focus-visible` instead of drawing a square outline outside it.
 * Also covers a wrapping label whose descendant is focused (phone field).
 *
 * Defined in `app/globals.css` so it cannot be purged — Tailwind does not
 * scan `src/` for class strings.
 */
export const FOCUS_VISIBLE_BORDER_CLASS = "a11y-focus-border"

/**
 * Visible indicator on a label when its visually-hidden input is focused.
 * Same mechanism as `FOCUS_VISIBLE_BORDER_CLASS`: the existing box border
 * turns black. An offset outline would be clipped inside accordion panels.
 */
export const FOCUS_WITHIN_CLASS = FOCUS_VISIBLE_BORDER_CLASS

/**
 * 2px ring drawn inside the control. Use when `overflow: hidden` would
 * clip the default offset, or when the control sits on a photo / unknown
 * background (an offset ring would be painted on the image).
 */
export const FOCUS_VISIBLE_INSET_CLASS = "a11y-focus-inset"

/**
 * Moves the ring from the focused link onto the card wrapping it. Use on a
 * card whose single link covers only part of its content (article tiles:
 * the link wraps image + heading, the teaser and "Read more" sit outside).
 *
 * Defined in `app/globals.css` so it cannot be purged — Tailwind does not
 * scan `src/` for class strings.
 */
export const FOCUS_VISIBLE_CARD_CLASS = "a11y-focus-card"
